import { useEffect, useState, useCallback } from "react";
import { MethodBadge } from "./Badge";

interface NavItem {
  id: string;
  label: string;
  method?: "GET" | "POST" | "PATCH" | "DELETE";
}

interface NavSection {
  id: string;
  label: string;
  items?: NavItem[];
}

interface SectionNavProps {
  sections: NavSection[];
}

export function SectionNav({ sections }: SectionNavProps) {
  const [activeId, setActiveId] = useState<string>("");

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const allIds: string[] = [];
    for (const section of sections) {
      allIds.push(section.id);
      if (section.items) {
        for (const item of section.items) {
          allIds.push(item.id);
        }
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-10% 0px -80% 0px", threshold: 0 },
    );

    for (const id of allIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  return (
    <nav className="sticky top-6 space-y-4">
      {sections.map((section) => (
        <div key={section.id} className="space-y-1">
          <button
            onClick={() => handleClick(section.id)}
            className={`block text-xs font-semibold uppercase tracking-wider transition-colors ${
              activeId === section.id
                ? "text-primary"
                : "text-base-content/40 hover:text-base-content/70"
            }`}
          >
            {section.label}
          </button>

          {section.items && (
            <ul className="space-y-0.5 ml-2 border-l border-base-content/10 pl-2">
              {section.items.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleClick(item.id)}
                    className={`flex items-center gap-1.5 w-full text-left py-0.5 text-sm transition-colors ${
                      activeId === item.id
                        ? "text-base-content font-medium"
                        : "text-base-content/50 hover:text-base-content/80"
                    }`}
                  >
                    {item.method && <MethodBadge method={item.method} />}
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </nav>
  );
}
